import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  signal
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { Community } from "../lot-community-search/lot-community-search.component";
import { OnlineIndicatorComponent } from "../../../../../shared/components/online-indicator/online-indicator.component";
import { DropdownModule } from "primeng/dropdown";
import { InspectionJobDTO } from "../../../../../domains/jobs/models/InspectionJobDTO";
import { environment } from "../../../../../core/config/environment";
import { firstValueFrom } from "rxjs";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { SkeletonModule } from "primeng/skeleton";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";
import { CustomerCareManagerScheduleService } from "../../../../../domains/jobs/services/customer-care-manager-schedule.service";
import { ProgressSpinner } from "primeng/progressspinner";
import { DateUtilsService } from "../../../../../shared/services/date.service";
import { OrdinalDatePipe } from "../../../../../shared/pipes/ordinal-date.pipe";
import { TagModule } from "primeng/tag";
import { SvgButtonComponent } from "../../../../../shared/components/search-button/svg-search-button.component";
import { AllMyLotsService } from "../../../../cm/pages/all-my-lots/services/all-my-lots.service";
import { CmLotService } from "../../../../cm/pages/all-my-lots/services/cm-lot.service";
import { ImageHydrationService } from "../../../../../core/sync/image-sync/image-hydration.service";
import { ConstructionManagerScheduleService } from "../../../../../domains/jobs/services/construction-manager-schedule.service";
import { VendorService } from "../../../../../domains/vendors/services/vendor-service";
import { JobWithLot } from "../../../../../domains/jobs/models/JobWithLot";
import { UserVendorContactDTO } from "../../../../../domains/vendors/models/UserVendorContactDTO";
import { VendorDTO } from "../../../../../domains/vendors/models/VendorDTO";

export enum LotStatus {
  Completed = "Completed",
  Overdue = "Overdue",
  InUserSchedule = "InUserSchedule",
  Scheduleable = "Scheduleable",
  Archived = "Archived"
}

export interface LotSearchResult {
  id: number;
  externalScheduleTaskId: number;
  externalLotId: number;
  externalDivisionId: number;
  externalFinancialCommunityId: number;
  lotBlock: string;
  salesCommunityName: string;
  streetAddress: string;
  customerCareManagerName: string;
  jobScheduleDate: Date;
  lotStatus: LotStatus;
}

@Component({
  selector: "lot-search-component",
  standalone: true,
  imports: [
    CommonModule,
    InputTextModule,
    FormsModule,
    DropdownModule,
    OnlineIndicatorComponent,
    SkeletonModule,
    ProgressSpinner,
    OrdinalDatePipe,
    TagModule,
    SvgButtonComponent
  ],
  templateUrl: "./lot-search.component.html",
  styleUrls: [
    "./lot-search.component.scss",
    "../lot-community-search/lot-community-search.component.scss"
  ]
})
export class LotSearchResults implements OnInit, AfterViewInit {
  @Input({ required: false })
  fromCMSearch = false;

  @Input({ required: true })
  selectedCommunity = signal<Community | undefined>(undefined);

  @Input({ required: true })
  communities: Community[];

  @Input({ required: true })
  searchTerm: string;

  @Output()
  backButtonPress = new EventEmitter<void>();

  @Output()
  searchCommunityHandler = new EventEmitter<void>();

  @Output()
  done = new EventEmitter<void>();

  @ViewChild("searchInput")
  private searchInputRef?: ElementRef<HTMLInputElement>;

  results = signal<LotSearchResult[]>([]);
  searchResultCommunity = signal<string>("");
  executedSearchTerm = signal<string>("");
  loadingJobs = signal<number[]>([]);
  unarchivingLots = signal<number[]>([]);

  searchBq7Jobs = `${environment.backend.apiBaseURL}/api/lot/SearchBq7JobsInSalesCommunity`;
  searchCmLots = `${environment.backend.apiBaseURL}/api/lot/SearchCmLotsInSalesCommunity`;
  addBq7Job = `${environment.backend.apiBaseURL}/api/job/AddBq7JobToUser`;
  addCmLot = `${environment.backend.apiBaseURL}/api/job/AddCmLotToUser`;
  isSearching = true;
  lotBlockSet = new Set<string>();
  public readonly LotStatus = LotStatus;

  private focusTimer: number | null = null;

  constructor(
    private http: HttpClient,
    private toastService: UiToastService,
    private customerCareManagerScheduleService: CustomerCareManagerScheduleService,
    private constructionManagerScheduleService: ConstructionManagerScheduleService,
    private vendorService: VendorService,
    private dateUtils: DateUtilsService,
    private allMyLotsService: AllMyLotsService,
    private cmLotService: CmLotService,
    private imageHydrationService: ImageHydrationService,
  ) { }

  async ngOnInit(): Promise<void> {
    if (this.fromCMSearch === false) {
      await this.executeSearch();
      return;
    }

    const allCMLots = await this.allMyLotsService.getAllMyLots();

    this.lotBlockSet = new Set(
      allCMLots
        .flatMap(community => community.lots)
        .map(lot => this.extractLotBlock(lot.address))
        .filter((lotBlock): lotBlock is string => lotBlock !== null)
        .map(lotBlock => this.normalizeLotBlock(lotBlock))
    );

    this.isSearching = false;
  }

  ngAfterViewInit(): void {
    this.focusSearchInput();
  }

  private focusSearchInput(): void {
    this.focusTimer = window.setTimeout(() => {
      const input = this.searchInputRef?.nativeElement;
      if (!input) {
        return;
      }

      input.focus({ preventScroll: true });

      if (input.value?.length) {
        input.select();
      }
    }, 0);
  }

  async executeSearchAll() {
    this.searchTerm = "";
    await this.executeSearch();
  }

  async executeSearch() {
    this.isSearching = true;

    const baseUrl = this.fromCMSearch ? this.searchCmLots : this.searchBq7Jobs;
    let url = `${baseUrl}/${this.selectedCommunity()?.externalSalesCommunityId}`;
    this.executedSearchTerm.set(this.searchTerm);

    if (!!this.executedSearchTerm()) {
      url = `${url}?searchTerm=${encodeURIComponent(this.executedSearchTerm())}`;
    }

    const searchResults = await firstValueFrom(this.http.get<LotSearchResult[]>(url));

    let normalizedResults = searchResults.map(result => ({
      ...result,
      jobScheduleDate: this.dateUtils.convertUtcDateToLocalDateOnly(result.jobScheduleDate)
    }));

    if (this.fromCMSearch) {
      normalizedResults = normalizedResults.map(result => {
        const exists = this.lotBlockSet.has(this.normalizeLotBlock(result.lotBlock));

        return exists && result.lotStatus === LotStatus.Completed
          ? { ...result, lotStatus: LotStatus.InUserSchedule }
          : result;
      });
    }

    this.results.set(normalizedResults);
    this.searchResultCommunity.set(this.selectedCommunity()!.salesCommunityName);

    this.isSearching = false;
  }

  canAddCompletedLot(result: LotSearchResult): boolean {
    return this.fromCMSearch && result.lotStatus === LotStatus.Completed;
  }

  private normalizeLotBlock(value: string | null | undefined): string {
    return (value ?? "").trim().toLowerCase();
  }

  private extractLotBlock(address: string): string | null {
    const match = address.match(/lot\s+([^-]+?)\s*-/i);
    return match ? match[1].trim() : null;
  }

  backToCommunitySearch() {
    this.results.set([]);
    this.backButtonPress.emit();
  }

  emitDone(): void {
    this.done.emit();
  }

  isAddingJob(result: LotSearchResult): boolean {
    return this.loadingJobs().includes(this.getScheduleKey(result));
  }

  // CM adds a whole lot (keyed by externalLotId); CCM adds a single BQ7 schedule task.
  private getScheduleKey(result: LotSearchResult): number {
    return this.fromCMSearch ? result.externalLotId : result.externalScheduleTaskId;
  }

  async addJob(result: LotSearchResult) {
    const scheduleKey = this.getScheduleKey(result);

    if (this.loadingJobs().includes(scheduleKey)) {
      return;
    }

    this.loadingJobs.update(jobs => [...jobs, scheduleKey]);

    try {
      const added = this.fromCMSearch
        ? await this.addCmLotToSchedule(result.externalLotId)
        : await this.addBq7JobToSchedule(result.externalScheduleTaskId);

      if (!added) {
        this.toastService.error("An error occurred while attempting to add this Job to your schedule");
        return;
      }

      this.results.update(items =>
        items.map(item =>
          (this.getScheduleKey(item) === scheduleKey
            ? { ...item, lotStatus: LotStatus.InUserSchedule }
            : item)
        )
      );

      this.toastService.success("Job successfully added to schedule");
    } catch (_) {
      this.toastService.error("An error occurred while attempting to add this Job to your schedule");
    } finally {
      this.loadingJobs.update(jobs => jobs.filter(id => id !== scheduleKey));
    }
  }

  private async addBq7JobToSchedule(externalScheduleTaskId: number): Promise<boolean> {
    const response = await firstValueFrom<HttpResponse<InspectionJobDTO>>(
      this.http.post<InspectionJobDTO>(this.addBq7Job, externalScheduleTaskId, { observe: "response" })
    );

    if (!response.ok || !response.body) {
      return false;
    }

    await this.customerCareManagerScheduleService.syncServerData({
      jobs: [response.body],
      userVendorContacts: [],
    });

    return true;
  }

  private async addCmLotToSchedule(externalLotId: number): Promise<boolean> {
    const response = await firstValueFrom<HttpResponse<JobWithLot[]>>(
      this.http.post<JobWithLot[]>(this.addCmLot, externalLotId, { observe: "response" })
    );

    const jobs = response.body;

    if (!response.ok || !jobs || jobs.length === 0) {
      return false;
    }

    const lotIds = Array.from(new Set(
      jobs
        .map(job => job.lotId ?? job.lot?.id)
        .filter((lotId): lotId is number => !!lotId)
    ));

    if (lotIds.length === 0) {
      return false;
    }

    // Mirrors DataSyncService.fetchAndUpdateConstructionManagerData: vendors can only be resolved once the
    // lot exists locally, so a newly added lot needs its vendor contacts fetched again after the sync.
    let vendors: VendorDTO[] | null = await this.vendorService.getVendorsForLotIds(lotIds);
    let userVendorContacts: UserVendorContactDTO[] = (vendors ?? []).flatMap(v => v.userVendorContacts ?? []);

    await this.constructionManagerScheduleService.syncServerData({ jobs }, userVendorContacts, false);

    if (vendors === null) {
      vendors = await this.vendorService.getVendorsForLotIds(lotIds);
      userVendorContacts = (vendors ?? []).flatMap(v => v.userVendorContacts ?? []);
      await this.constructionManagerScheduleService.syncUserVendorContactData(userVendorContacts);
      await this.constructionManagerScheduleService.cleanupUserVendorContacts(userVendorContacts);
    }

    await this.imageHydrationService.hydrateByJobIds(
      jobs.filter(job => job.isComplete === true).map(job => job.id)
    );

    return true;
  }

  async unarchiveLot(lotId: number): Promise<void> {
    if (this.unarchivingLots().includes(lotId)) {
      return;
    }

    this.unarchivingLots.update(ids => [...ids, lotId]);

    try {
      const success = await this.cmLotService.unarchiveLot(lotId);
      if (success) {
        // Re-execute the search so the API determines the correct post-unarchive status.
        await this.executeSearch();
      }
    } finally {
      this.unarchivingLots.update(ids => ids.filter(id => id !== lotId));
    }
  }
}